import { useId, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/shared/utils/cn'
import { SidebarNavLeaf } from './SidebarNavLeaf'
import type { NavGroup } from './navigation'

interface SidebarNavGroupProps {
  group: NavGroup
}

/**
 * Accordion del sidebar. El header es un `<button>` con `aria-expanded` /
 * `aria-controls`. Auto-expand al montar si la ruta actual está dentro del
 * grupo (calculado UNA vez, no fuerza re-expand al navegar).
 *
 * La animación usa el truco de `grid-template-rows` para evitar medir altura
 * en JS.
 */
export function SidebarNavGroup({ group }: SidebarNavGroupProps) {
  const location = useLocation()
  const panelId = useId()

  const isInside = location.pathname.startsWith(group.matchPath)
  const [open, setOpen] = useState(isInside)

  return (
    <div className="mb-0.5">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'group mx-3 flex w-[calc(100%-1.5rem)] items-center gap-3 rounded-lg px-3 py-2.5 text-[13.5px] font-medium transition-colors',
          isInside
            ? 'text-white hover:bg-sidebar-hover'
            : 'text-[#b7bdc8] hover:bg-sidebar-hover hover:text-white',
        )}
      >
        <group.icon className="h-[18px] w-[18px]" />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-ink-subtle transition-transform',
            open && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      <div
        id={panelId}
        role="region"
        aria-hidden={!open}
        data-open={open}
        className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out data-[open=true]:grid-rows-[1fr]"
      >
        <div className="overflow-hidden">
          <div className="pt-1">
            {group.children.map((child) => (
              <SidebarNavLeaf key={child.id} item={child} nested />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
